import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
	Bolt,
	Code as CodeIcon,
	Article as DetailsIcon,
	Class as EntityIcon,
	FolderOutlined as NamespaceFolderIcon,
	ListAlt as EnumIcon,
	AccountTree as GraphIcon,
	Hub,
	Inventory2,
	Mail,
	PlayArrow,
	SettingsApplications,
} from '@mui/icons-material';
import { Box, Chip, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';

import { usePersistedSetting } from '@@common';

import {
	CodeBaseStatesModel,
	CodeStoreFileModel,
	ContextModel,
	OperationExecutionTypeModel,
	OperationExecutionTypesModel,
	OperationExposureModel,
	SchemaTypes,
	SchemaTypesModel,
} from '../../api/api';
import { ArtifactCategory, ArtifactItem, ArtifactPanel } from '../../common/components/ArtifactPanel';
import Spinner from '../../common/components/Spinner';
import { useSearchParamsEx } from '../../common/hooks/useSearchParamsEx';
import { isChangeChatMessage } from '../../components/Chat/isChangeChatMessage';
import { ChatMessage } from '../../components/Chat/PromptChatAdapter';
import PromptChatPanel from '../../components/Chat/PromptChatPanel';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useCodeStoreController } from '../CodeStore/Hooks/useCodeStoreController';
import EnumSection from '../EntityDesigner/Components/EnumSection';
import SchemaSection from '../EntityDesigner/Components/SchemaSection';
import { useEntityMutations } from '../EntityDesigner/Hooks/useEntityController';
import { useEnumMutations } from '../EntityDesigner/Hooks/useEnumController';
import { useEventHandlerController } from '../EventHandlerDesigner/Hooks/useEventHandlerController';
import { useIntegrationEventsController } from '../IntegrationEventDesigner/Hooks/useIntegrationEventsController';
import { useOperationController } from '../OperationDesigner/Hooks/useOperationController';
import ArtifactDeploymentState from './Components/ArtifactDeploymentState';
import ContextArtifactDialogs from './Components/ContextArtifactDialogs';
import ContextCodeFileItem from './Components/ContextCodeFileItem';
import DomainGraphToolbar from './Components/DomainGraphToolbar';
import { getArtifactChatContext, getContextArtifactId, isApplicationArtifact, parseContextArtifactId } from './ContextArtifactSelection';
import ContextServiceView from './ContextServiceView';
import DomainEntityGraphView, { DEFAULT_NODE_FILTERS } from './DomainEntityGraphView';
import DomainEventHandlersView from './DomainEventHandlersView';
import { DomainGraphNodeFilter } from './DomainGraphUtils';
import { useContextArtifactLayout } from './Hooks/useContextArtifactLayout';
import { useContextCodeArtifacts } from './Hooks/useContextCodeArtifacts';
import { ContextDesignerCodeSnapshotModel } from './Hooks/useContextDesignerSnapshot';
import { useDomainModel } from './Hooks/useDomainModel';
import IntegrationEventArtifactView from './IntegrationEventArtifactView';
import { getTestArtifactLayer } from './TestArtifactLayer';

type ContextDomainViewProps = {
	contextId: string;
	/** Refreshes the parent designer snapshot and all context-scoped queries. */
	onRefreshContext?: () => Promise<void>;
	/** Incremented by parent after a domain codebase advancement to trigger code-file refetch. */
	refetchTrigger?: number;
	applicationRefetchTrigger?: number;
	contextRefreshTrigger?: number;
	initialContext?: ContextModel | null;
	initialOperationExecutionTypes?: OperationExecutionTypeModel[];
	initialCodeSnapshot?: ContextDesignerCodeSnapshotModel | null;
	domainCodebaseState?: CodeBaseStatesModel;
	applicationCodebaseState?: CodeBaseStatesModel;
	activeDeploymentLayer: 'domain' | 'application';
	onActiveDeploymentLayerChange: (layer: 'domain' | 'application') => void;
};

type CodeFileDeleteTarget = {
	fileName: string;
	codeBaseId?: string;
};

export default function ContextDomainView({
	contextId,
	onRefreshContext,
	refetchTrigger,
	applicationRefetchTrigger,
	contextRefreshTrigger,
	initialContext,
	initialOperationExecutionTypes,
	initialCodeSnapshot,
	domainCodebaseState,
	applicationCodebaseState,
	activeDeploymentLayer,
	onActiveDeploymentLayerChange,
}: ContextDomainViewProps) {
	const [searchParams, setSearchParam] = useSearchParamsEx();
	const selectedItemId = searchParams.get('sid') ?? null;
	const setSelectedItemId = useCallback((itemId: string | null) => setSearchParam('sid', itemId ?? undefined), [setSearchParam]);

	// Dialog states
	const [isCreatingEntity, setIsCreatingEntity] = useState(false);
	const [isCreatingEnum, setIsCreatingEnum] = useState(false);
	const [isCreatingOperation, setIsCreatingOperation] = useState(false);
	const [isCreatingEventHandler, setIsCreatingEventHandler] = useState(false);
	const [isCreatingIntegrationEvent, setIsCreatingIntegrationEvent] = useState(false);
	const [operationExecutionTypes, setOperationExecutionTypes] = useState<OperationExecutionTypeModel[]>(initialOperationExecutionTypes ?? []);
	const [entityName, setEntityName] = useState('');
	const [entityDescription, setEntityDescription] = useState('');
	const [enumName, setEnumName] = useState('');
	const [enumDescription, setEnumDescription] = useState('');
	const [codeFileDeleteTarget, setCodeFileDeleteTarget] = useState<CodeFileDeleteTarget | null>(null);
	const [domainViewMode, setDomainViewMode] = usePersistedSetting<'entity' | 'graph'>('context-domain.viewMode', 'entity');
	const [schemaRefreshVersion, setSchemaRefreshVersion] = useState(0);
	const [graphNodeFilters, setGraphNodeFilters] = useState<DomainGraphNodeFilter[]>(DEFAULT_NODE_FILTERS);
	const [graphSearchText, setGraphSearchText] = useState('');
	const [autoLayoutRequest, setAutoLayoutRequest] = useState(0);

	// Use shared code base info hook with React Query caching

	const { data: domainModel, isLoading: loadingDomainModel, refetch: refreshDomainModel } = useDomainModel(contextId);

	const { createFolder, renameFolder, deleteFolder, moveArtifact, getFolderId, foldersByCategory } = useContextArtifactLayout(contextId);
	const { createEnum, deleteEnum } = useEnumMutations(contextId);
	const { createRootEntity } = useEntityMutations(contextId);
	const domainEntities = useMemo(() => domainModel?.entities ?? [], [domainModel?.entities]);
	const entityArtifactItems = useMemo<ArtifactItem[]>(() => {
		const aggregateRoots = domainEntities.filter((entity) => entity.schemaTypeId === SchemaTypesModel.RootDataSchema);
		const aggregateRootIds = new Set(aggregateRoots.map((entity) => entity.id));
		const childrenByRootId = new Map<string, ArtifactItem[]>();
		const rootItems: ArtifactItem[] = [];

		for (const entity of domainEntities) {
			const aggregateRootId =
				entity.schemaTypeId === SchemaTypesModel.RootDataSchema
					? undefined
					: (entity.properties.find(
							(property) => property.isReferenceProperty && property.referenceSchemaId && aggregateRootIds.has(property.referenceSchemaId),
						)?.referenceSchemaId ??
						aggregateRoots.find((root) => root.properties.some((property) => property.isReferenceProperty && property.referenceSchemaId === entity.id))?.id);
			const item: ArtifactItem = {
				id: getContextArtifactId({ kind: 'entity', id: entity.id }),
				label: entity.name,
				subFilterValue: getEntitySubFilterValue(entity.schemaTypeId),
				endAdornment:
					entity.schemaTypeId === SchemaTypesModel.RootDataSchema ? (
						<Tooltip title="Aggregate root">
							<Chip label="Root" size="small" color="primary" variant="outlined" sx={{ height: 16, fontSize: '0.625rem', '& .MuiChip-label': { px: 0.6 } }} />
						</Tooltip>
					) : undefined,
				folderId: getFolderId('Entity', entity.id),
			};

			if (aggregateRootId) {
				childrenByRootId.set(aggregateRootId, [...(childrenByRootId.get(aggregateRootId) ?? []), item]);
			} else {
				rootItems.push(item);
			}
		}

		return rootItems.map((item) => ({
			...item,
			children: childrenByRootId.get(parseContextArtifactId(item.id)?.id ?? item.id),
		}));
	}, [domainEntities, getFolderId]);
	const enums = useMemo(() => domainModel?.enums ?? [], [domainModel?.enums]);
	const { operations, loadingOperations, createOperation, getOperationExecutionTypes, refreshOperations } = useOperationController(contextId);
	const { eventHandlers, loadingEventHandlers, createEventHandler, updateEventHandler, deleteEventHandler, availableEventsToHandle, refreshEventHandlers } =
		useEventHandlerController(contextId);
	const { integrationEvents, loadingIntegrationEvents, createIntegrationEvent, updateIntegrationEvent, deleteIntegrationEvent, reloadIntegrationEvents } =
		useIntegrationEventsController(contextId);

	const { deleteCodeFile } = useCodeStoreController();
	const { data: codeArtifacts, isLoading: loadingCode, refetch: fetchCodeFiles } = useContextCodeArtifacts(contextId, initialCodeSnapshot);
	const codeFiles = useMemo(() => codeArtifacts?.domainFiles ?? [], [codeArtifacts?.domainFiles]);
	const applicationCodeFiles = useMemo(() => codeArtifacts?.applicationFiles ?? [], [codeArtifacts?.applicationFiles]);
	const testCodeFiles = useMemo(() => codeArtifacts?.testFiles ?? [], [codeArtifacts?.testFiles]);
	const domainCodeBase = codeArtifacts?.domainCodeBase;
	const domainTestCodeFiles = useMemo(
		() => testCodeFiles.filter((file) => getTestArtifactLayer(file, codeArtifacts?.domainCodeBase, codeArtifacts?.applicationCodeBase) === 'domain'),
		[testCodeFiles, codeArtifacts?.applicationCodeBase, codeArtifacts?.domainCodeBase],
	);
	const applicationTestCodeFiles = useMemo(
		() => testCodeFiles.filter((file) => getTestArtifactLayer(file, codeArtifacts?.domainCodeBase, codeArtifacts?.applicationCodeBase) === 'application'),
		[testCodeFiles, codeArtifacts?.applicationCodeBase, codeArtifacts?.domainCodeBase],
	);
	const refreshCodeArtifacts = useCallback(async () => {
		await fetchCodeFiles();
	}, [fetchCodeFiles]);

	const refreshArtifactsModel = useCallback(async () => {
		await Promise.all([refreshDomainModel(), refreshOperations(), refreshEventHandlers(), reloadIntegrationEvents(), refreshCodeArtifacts()]);
	}, [refreshCodeArtifacts, refreshDomainModel, refreshEventHandlers, refreshOperations, reloadIntegrationEvents]);

	const handleAssistantMessageReceived = useCallback(
		async (message: ChatMessage) => {
			if (isChangeChatMessage(message)) {
				await (onRefreshContext ?? refreshArtifactsModel)();
				if (!onRefreshContext) {
					setSchemaRefreshVersion((version) => version + 1);
				}
			}
		},
		[onRefreshContext, refreshArtifactsModel],
	);

	useEffect(() => {
		if (contextRefreshTrigger) {
			setSchemaRefreshVersion((version) => version + 1);
		}
	}, [contextRefreshTrigger]);

	useEffect(() => {
		if (!initialOperationExecutionTypes?.length) {
			getOperationExecutionTypes().then(setOperationExecutionTypes);
		}
	}, [getOperationExecutionTypes, initialOperationExecutionTypes]);

	// Refetch code files when parent signals a domain codebase advancement
	useEffect(() => {
		if (refetchTrigger || applicationRefetchTrigger) {
			refreshCodeArtifacts();
		}
	}, [applicationRefetchTrigger, refetchTrigger, refreshCodeArtifacts]);

	// Handler to refresh a single code file
	const handleRefreshCodeFile = useCallback(
		async (_fileName: string) => {
			await refreshCodeArtifacts();
		},
		[refreshCodeArtifacts],
	);

	const selection = useMemo(() => parseContextArtifactId(selectedItemId), [selectedItemId]);
	const selectedEntity = selection?.kind === 'entity' ? domainEntities.find((entity) => entity.id === selection.id) : undefined;
	const selectedEnum = selection?.kind === 'enum' ? enums.find((enumModel) => enumModel.id === selection.id) : undefined;
	const selectedCodeFile = selection?.kind === 'domain-file' ? codeFiles.find((file) => file.id === selection.id) : undefined;
	const selectedDomainTestCodeFile = selection?.kind === 'domain-test-file' ? testCodeFiles.find((file) => file.id === selection.id) : undefined;
	const selectedOperation = selection?.kind === 'operation' ? operations.find((operation) => operation.id === selection.id) : undefined;
	const selectedEventHandler = selection?.kind === 'event-handler' ? eventHandlers.find((handler) => handler.handler.id === selection.id) : undefined;
	const selectedIntegrationEvent = selection?.kind === 'integration-event' ? integrationEvents.find((event) => event.event.id === selection.id) : undefined;
	const selectedApplicationCodeFile =
		selection?.kind === 'application-file'
			? applicationCodeFiles.find((file) => file.id === selection.id)
			: selection?.kind === 'test-file'
				? testCodeFiles.find((file) => file.id === selection.id)
				: undefined;
	const selectedArtifactCodeFile = selectedCodeFile ?? selectedDomainTestCodeFile ?? selectedApplicationCodeFile;
	const isApplicationArtifactSelected = isApplicationArtifact(selection);
	const showsApplicationWorkspace = selection ? ['operation', 'application-file', 'test-file'].includes(selection.kind) : false;
	const loadingArtifacts = loadingCode || loadingDomainModel || loadingOperations || loadingEventHandlers || loadingIntegrationEvents;
	const selectedArtifactExists =
		!selection ||
		loadingArtifacts ||
		Boolean(
			selectedEntity ||
			selectedEnum ||
			selectedCodeFile ||
			selectedDomainTestCodeFile ||
			selectedOperation ||
			selectedEventHandler ||
			selectedIntegrationEvent ||
			selectedApplicationCodeFile,
		);

	useEffect(() => {
		if (selection && !loadingArtifacts && !selectedArtifactExists) {
			setSelectedItemId(null);
		}
	}, [loadingArtifacts, selectedArtifactExists, selection, setSelectedItemId]);

	const requestDeleteCodeFile = useCallback((fileName: string, codeBaseId?: string) => {
		if (!codeBaseId) {
			return;
		}

		setCodeFileDeleteTarget({ fileName, codeBaseId });
	}, []);

	const handleDeleteEnum = useCallback(
		async (enumId: string) => {
			await deleteEnum(enumId);
			if (selection?.kind === 'enum' && selection.id === enumId) {
				setSelectedItemId(null);
			}
		},
		[deleteEnum, selection, setSelectedItemId],
	);

	const handleConfirmDeleteCodeFile = useCallback(
		async (fileName: string, codeBaseId?: string) => {
			if (!codeBaseId) {
				return;
			}

			await deleteCodeFile(fileName, codeBaseId);
			if (selectedArtifactCodeFile?.name === fileName) {
				setSelectedItemId(null);
			}
			await refreshCodeArtifacts();
		},
		[deleteCodeFile, refreshCodeArtifacts, selectedArtifactCodeFile?.name, setSelectedItemId],
	);

	const handleDeleteCodeFileConfirmed = useCallback(async () => {
		if (!codeFileDeleteTarget) {
			return;
		}

		try {
			await handleConfirmDeleteCodeFile(codeFileDeleteTarget.fileName, codeFileDeleteTarget.codeBaseId);
		} finally {
			setCodeFileDeleteTarget(null);
		}
	}, [codeFileDeleteTarget, handleConfirmDeleteCodeFile]);

	// Start a populated context in its graph overview. A selected artifact always takes precedence.
	useEffect(() => {
		if (!selectedItemId && domainEntities.length > 0) {
			setDomainViewMode('graph');
		}
	}, [selectedItemId, domainEntities, setDomainViewMode]);

	// Handle entity creation
	const handleCreateEntity = useCallback(async () => {
		if (!entityName.trim()) return;
		await createRootEntity(entityName.trim(), entityDescription.trim());
		setIsCreatingEntity(false);
		setEntityName('');
		setEntityDescription('');
	}, [entityName, entityDescription, createRootEntity]);

	// Handle enum creation
	const handleCreateEnum = useCallback(async () => {
		if (!enumName.trim()) return;
		await createEnum(enumName.trim(), enumDescription.trim());
		setIsCreatingEnum(false);
		setEnumName('');
		setEnumDescription('');
	}, [enumName, enumDescription, createEnum]);

	const handleCreateEventHandler = useCallback(
		async (integrationEventId: string, name?: string, description?: string) => {
			const handler = await createEventHandler(integrationEventId, name, description);
			setIsCreatingEventHandler(false);
			if (handler?.id) {
				setSelectedItemId(getContextArtifactId({ kind: 'event-handler', id: handler.id }));
			}
		},
		[createEventHandler, setSelectedItemId],
	);

	const handleCreateIntegrationEvent = useCallback(
		async (name: string, description: string, targetContextId: string) => {
			const integrationEvent = await createIntegrationEvent(name, description, targetContextId);
			setIsCreatingIntegrationEvent(false);
			if (integrationEvent?.id) {
				setSelectedItemId(getContextArtifactId({ kind: 'integration-event', id: integrationEvent.id }));
			}
		},
		[createIntegrationEvent, setSelectedItemId],
	);

	const handleUpdateIntegrationEventTags = useCallback(
		async (eventId: string, tags: string[]) => {
			const integrationEvent = integrationEvents.find((event) => event.event.id === eventId)?.event;
			if (!integrationEvent) {
				return;
			}

			await updateIntegrationEvent(eventId, {
				name: integrationEvent.name,
				description: integrationEvent.description ?? '',
				tags,
			});
			await reloadIntegrationEvents();
		},
		[integrationEvents, reloadIntegrationEvents, updateIntegrationEvent],
	);

	const handleDeleteIntegrationEvent = useCallback(
		async (eventId: string) => {
			await deleteIntegrationEvent(eventId);
			setSelectedItemId(null);
			await reloadIntegrationEvents();
		},
		[deleteIntegrationEvent, reloadIntegrationEvents, setSelectedItemId],
	);

	const handleCreateOperation = useCallback(
		async (
			operationName: string,
			description: string,
			executionType: OperationExecutionTypesModel,
			exposure: OperationExposureModel,
			selectedEntityIds: string[],
		) => {
			const operationId = await createOperation(operationName, description, executionType, exposure, selectedEntityIds);
			setIsCreatingOperation(false);
			if (typeof operationId === 'string') {
				setSelectedItemId(getContextArtifactId({ kind: 'operation', id: operationId }));
			}
			return operationId;
		},
		[createOperation, setSelectedItemId],
	);

	const handleGraphNodeFilterChanged = useCallback((nextFilters: DomainGraphNodeFilter[]) => {
		setGraphNodeFilters(nextFilters);
	}, []);

	// Build artifact categories
	const categories = useMemo<ArtifactCategory[]>(
		() => [
			{
				id: 'domain-root',
				label: 'Domain Model',
				folderArtifactType: 'Implementation',
				icon: <Hub fontSize="small" />,
				endAdornment: (
					<ArtifactDeploymentState
						layerLabel="Domain Model"
						state={domainCodebaseState}
						selected={activeDeploymentLayer === 'domain'}
						onSelect={() => onActiveDeploymentLayerChange('domain')}
					/>
				),
				items: buildNamespacedCodeFileItems(codeFiles, 'Domain', 'domain-root', (file) => ({
					id: getContextArtifactId({ kind: 'domain-file', id: file.id }),
					label: file.name,
					folderId: getFolderId('DomainCodeFile', file.id),
					onDelete: () => requestDeleteCodeFile(file.name, domainCodeBase?.id),
				})),
				folders: foldersByCategory.implementation,
				onCreateFolder: (name) => createFolder('implementation', name),
				onRenameFolder: renameFolder,
				onDeleteFolder: deleteFolder,
				onMoveItemToFolder: (itemId, folderId) => moveArtifact('DomainCodeFile', parseContextArtifactId(itemId)?.id ?? itemId, folderId),
			},
			{
				id: 'entities',
				parentId: 'domain-root',
				label: 'Entities',
				artifactType: 'Entities',
				icon: <EntityIcon fontSize="small" />,
				items: entityArtifactItems,
				onAddItem: () => setIsCreatingEntity(true),
				addItemLabel: 'Create Entity',
				folders: foldersByCategory.entities,
				onCreateFolder: (name) => createFolder('entities', name),
				onRenameFolder: renameFolder,
				onDeleteFolder: deleteFolder,
				onMoveItemToFolder: (itemId, folderId) => moveArtifact('Entity', parseContextArtifactId(itemId)?.id ?? itemId, folderId),
			},
			{
				id: 'enums',
				parentId: 'domain-root',
				label: 'Enums',
				artifactType: 'Enums',
				icon: <EnumIcon fontSize="small" />,
				items: (enums ?? []).map((enumModel) => ({
					id: getContextArtifactId({ kind: 'enum', id: enumModel.id }),
					label: enumModel.name,
					folderId: getFolderId('Enum', enumModel.id),
					onDelete: () => handleDeleteEnum(enumModel.id),
				})),
				onAddItem: () => setIsCreatingEnum(true),
				addItemLabel: 'Create Enum',
				folders: foldersByCategory.enums,
				onCreateFolder: (name) => createFolder('enums', name),
				onRenameFolder: renameFolder,
				onDeleteFolder: deleteFolder,
				onMoveItemToFolder: (itemId, folderId) => moveArtifact('Enum', parseContextArtifactId(itemId)?.id ?? itemId, folderId),
			},
			{
				id: 'domain-tests',
				parentId: 'domain-root',
				label: 'Tests',
				artifactType: 'Tests',
				icon: <CodeIcon fontSize="small" />,
				items: domainTestCodeFiles.map((file) => ({
					id: getContextArtifactId({ kind: 'domain-test-file', id: file.id }),
					label: file.name,
					folderId: getFolderId('TestCodeFile', file.id),
					onDelete: () => requestDeleteCodeFile(file.name, codeArtifacts?.testCodeBase?.id),
				})),
				emptyLabel: 'No domain tests generated yet',
				folders: foldersByCategory['domain-tests'],
				onCreateFolder: (name) => createFolder('domain-tests', name),
				onRenameFolder: renameFolder,
				onDeleteFolder: deleteFolder,
				onMoveItemToFolder: (itemId, folderId) => moveArtifact('TestCodeFile', parseContextArtifactId(itemId)?.id ?? itemId, folderId),
			},
			{
				id: 'application-root',
				label: 'Application',
				folderArtifactType: 'Implementation',
				icon: <SettingsApplications fontSize="small" />,
				endAdornment: (
					<ArtifactDeploymentState
						layerLabel="Application"
						state={applicationCodebaseState}
						selected={activeDeploymentLayer === 'application'}
						onSelect={() => onActiveDeploymentLayerChange('application')}
					/>
				),
				items: buildNamespacedCodeFileItems(applicationCodeFiles, 'Application', 'application-root', (file) => ({
					id: getContextArtifactId({ kind: 'application-file', id: file.id }),
					label: file.name,
					folderId: getFolderId('ApplicationCodeFile', file.id),
					onDelete: () => requestDeleteCodeFile(file.name, codeArtifacts?.applicationCodeBase?.id),
				})),
				folders: foldersByCategory.codeview,
				onCreateFolder: (name) => createFolder('codeview', name),
				onRenameFolder: renameFolder,
				onDeleteFolder: deleteFolder,
				onMoveItemToFolder: (itemId, folderId) => moveArtifact('ApplicationCodeFile', parseContextArtifactId(itemId)?.id ?? itemId, folderId),
			},
			{
				id: 'operations',
				parentId: 'application-root',
				label: 'Operations',
				artifactType: 'Operations',
				icon: <PlayArrow fontSize="small" />,
				items: operations.map((operation) => ({ id: getContextArtifactId({ kind: 'operation', id: operation.id }), label: operation.name })),
				onAddItem: () => setIsCreatingOperation(true),
				addItemLabel: 'Create Operation',
			},
			{
				id: 'integration-events',
				parentId: 'application-root',
				label: 'Integration Events',
				artifactType: 'Integration Events',
				icon: <Mail fontSize="small" />,
				items: integrationEvents.map((event) => ({
					id: getContextArtifactId({ kind: 'integration-event', id: event.event.id }),
					label: event.event.name,
					onDelete: () => handleDeleteIntegrationEvent(event.event.id),
				})),
				emptyLabel: 'No integration events yet',
				onAddItem: () => setIsCreatingIntegrationEvent(true),
				addItemLabel: 'Create Integration Event',
			},
			{
				id: 'event-handlers',
				parentId: 'application-root',
				label: 'Integration Event Handlers',
				artifactType: 'Event Handlers',
				icon: <Bolt fontSize="small" />,
				items: eventHandlers.map((handler) => ({
					id: getContextArtifactId({ kind: 'event-handler', id: handler.handler.id }),
					label: handler.handler.name,
				})),
				emptyLabel: 'No integration event handlers yet',
				onAddItem: () => setIsCreatingEventHandler(true),
				addItemLabel: 'Create Integration Event Handler',
			},
			{
				id: 'operation-tests',
				parentId: 'application-root',
				label: 'Tests',
				artifactType: 'Tests',
				icon: <CodeIcon fontSize="small" />,
				items: applicationTestCodeFiles.map((file) => ({
					id: getContextArtifactId({ kind: 'test-file', id: file.id }),
					label: file.name,
					folderId: getFolderId('TestCodeFile', file.id),
					onDelete: () => requestDeleteCodeFile(file.name, codeArtifacts?.testCodeBase?.id),
				})),
				emptyLabel: 'No application tests generated yet',
				folders: foldersByCategory['application-tests'] ?? foldersByCategory.tests,
				onCreateFolder: (name) => createFolder('application-tests', name),
				onRenameFolder: renameFolder,
				onDeleteFolder: deleteFolder,
				onMoveItemToFolder: (itemId, folderId) => moveArtifact('TestCodeFile', parseContextArtifactId(itemId)?.id ?? itemId, folderId),
			},
		],
		[
			activeDeploymentLayer,
			applicationCodeFiles,
			applicationCodebaseState,
			applicationTestCodeFiles,
			codeArtifacts?.applicationCodeBase?.id,
			codeArtifacts?.testCodeBase?.id,
			codeFiles,
			createFolder,
			deleteFolder,
			domainCodeBase?.id,
			domainCodebaseState,
			entityArtifactItems,
			domainTestCodeFiles,
			enums,
			eventHandlers,
			foldersByCategory,
			getFolderId,
			handleDeleteEnum,
			handleDeleteIntegrationEvent,
			integrationEvents,
			moveArtifact,
			onActiveDeploymentLayerChange,
			operations,
			renameFolder,
			requestDeleteCodeFile,
		],
	);

	return (
		<Stack direction="column" sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
			<Stack
				direction="row"
				sx={{
					flexGrow: 1,
					minHeight: 0,
					overflow: 'hidden',
					gap: (theme) => `${theme.layout.panelGap}px`,
					px: (theme) => `${theme.layout.edgeGap}px`,
					py: (theme) => `${theme.layout.edgeGap}px`,
				}}
			>
				{/* Artifact Panel on the left */}
				<ArtifactPanel
					title="Artifacts"
					titleIcon={<Inventory2 />}
					categories={categories}
					selectedItemId={selectedItemId}
					onSelectItem={setSelectedItemId}
					searchPlaceholder="Search artifacts…"
					showArtifactTypeFilters
					subFilter={{
						categoryId: 'entities',
						label: 'Entity type',
						options: [
							{ value: 'root', label: 'Root' },
							{ value: 'domain', label: 'Domain' },
							{ value: 'dto', label: 'DTO' },
						],
					}}
					defaultWidth={300}
					storageKey="context-designer-panel-width"
				/>

				{/* Main content area */}
				<Paper
					sx={{
						flex: 1,
						minHeight: 0,
						overflow: selectedEntity || isApplicationArtifactSelected || selectedCodeFile || selectedDomainTestCodeFile ? 'hidden' : 'auto',
						position: 'relative',
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					{loadingArtifacts && <Spinner />}
					{!selectedCodeFile && !selectedDomainTestCodeFile && !isApplicationArtifactSelected && !selectedEventHandler && !selectedIntegrationEvent && (
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: 'auto 1fr auto',
								alignItems: 'center',
								gap: 1,
								minHeight: 60,
								px: 2,
								py: 1.25,
							}}
						>
							{domainViewMode === 'graph' ? (
								<DomainGraphToolbar
									enabledNodeFilters={graphNodeFilters}
									searchText={graphSearchText}
									onNodeFiltersChanged={handleGraphNodeFilterChanged}
									onSearchTextChanged={setGraphSearchText}
									onAutoLayout={() => setAutoLayoutRequest((request) => request + 1)}
								/>
							) : (
								<Box />
							)}
							<Box />
							<ToggleButtonGroup
								value={domainViewMode}
								exclusive
								size="small"
								onChange={(_event, nextMode) => {
									if (nextMode) {
										setDomainViewMode(nextMode);
									}
								}}
								aria-label="domain entity view mode"
								sx={{ justifySelf: 'end' }}
							>
								<ToggleButton value="entity" aria-label="Entity details" title="Entity details">
									<DetailsIcon fontSize="small" />
								</ToggleButton>
								<ToggleButton value="graph" aria-label="Entity graph" title="Entity graph">
									<GraphIcon fontSize="small" />
								</ToggleButton>
							</ToggleButtonGroup>
						</Box>
					)}
					{/* Content area */}
					{!selectedArtifactExists ? (
						<Box sx={{ p: 3 }}>
							<Typography variant="h6">Artifact not found</Typography>
							<Typography variant="body2" color="text.secondary">
								The selected artifact no longer exists. Choose another artifact from the tree.
							</Typography>
						</Box>
					) : showsApplicationWorkspace ? (
						<ContextServiceView
							contextId={contextId}
							embedded
							initialContext={initialContext}
							initialOperationExecutionTypes={operationExecutionTypes}
							initialCodeSnapshot={initialCodeSnapshot}
							availableEntities={domainEntities}
						/>
					) : selectedEventHandler ? (
						<DomainEventHandlersView
							contextId={contextId}
							embedded
							selectedHandlerId={selectedEventHandler.handler.id}
							handlers={eventHandlers}
							availableEvents={availableEventsToHandle}
							applicationCodeFiles={applicationCodeFiles}
							applicationCodeBase={codeArtifacts?.applicationCodeBase}
							onCreateHandler={handleCreateEventHandler}
							onUpdateHandler={(eventHandlerId, name, description, tags) => updateEventHandler(eventHandlerId, { name, description, tags })}
							onDeleteHandler={async (eventHandlerId) => {
								await deleteEventHandler(eventHandlerId);
								await refreshEventHandlers();
								setSelectedItemId(null);
							}}
							onRefreshCodeArtifacts={refreshCodeArtifacts}
							onSelectHandler={(handlerId) => setSelectedItemId(getContextArtifactId({ kind: 'event-handler', id: handlerId }))}
						/>
					) : selectedIntegrationEvent ? (
						<IntegrationEventArtifactView
							schemaRefreshVersion={schemaRefreshVersion}
							integrationEvent={selectedIntegrationEvent}
							availableEnums={enums}
							onUpdateTags={(tags) => handleUpdateIntegrationEventTags(selectedIntegrationEvent.event.id, tags)}
							onDelete={() => handleDeleteIntegrationEvent(selectedIntegrationEvent.event.id)}
						/>
					) : domainViewMode === 'graph' && !selectedCodeFile && !selectedDomainTestCodeFile ? (
						<DomainEntityGraphView
							contextId={contextId}
							entities={domainEntities}
							enums={enums}
							selectedEntityId={selectedEntity?.id}
							selectedEnumId={selectedEnum?.id}
							enabledNodeFilters={graphNodeFilters}
							searchText={graphSearchText}
							autoLayoutRequest={autoLayoutRequest}
							onEntitySelected={(entityId) => setSelectedItemId(getContextArtifactId({ kind: 'entity', id: entityId }))}
							onEnumSelected={(enumId) => setSelectedItemId(getContextArtifactId({ kind: 'enum', id: enumId }))}
						/>
					) : selectedEntity ? (
						// Entity details view
						<Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
							<SchemaSection schema={selectedEntity} availableEnums={enums} fillAvailableHeight refreshVersion={schemaRefreshVersion} />
						</Box>
					) : selectedEnum ? (
						// Enum details view
						<EnumSection enumModel={selectedEnum} />
					) : selectedCodeFile ? (
						// Code file view
						<ContextCodeFileItem
							file={selectedCodeFile}
							alwaysExpanded
							onRefresh={handleRefreshCodeFile}
							onDelete={(fileName) => requestDeleteCodeFile(fileName, domainCodeBase?.id)}
						/>
					) : selectedDomainTestCodeFile ? (
						<ContextCodeFileItem
							file={selectedDomainTestCodeFile}
							alwaysExpanded
							onRefresh={handleRefreshCodeFile}
							onDelete={(fileName) => requestDeleteCodeFile(fileName, codeArtifacts?.testCodeBase?.id)}
						/>
					) : (
						<Box sx={{ flex: 1, display: 'grid', placeItems: 'center', p: 3 }}>
							<Stack
								alignItems="center"
								spacing={1.5}
								sx={{
									width: '100%',
									maxWidth: 400,
									p: 3.5,
									textAlign: 'center',
									border: '1px dashed',
									borderColor: 'divider',
									borderRadius: 2,
									bgcolor: 'action.hover',
								}}
							>
								<Box
									sx={{
										display: 'grid',
										placeItems: 'center',
										width: 48,
										height: 48,
										borderRadius: '50%',
										color: 'primary.main',
										bgcolor: 'background.paper',
										boxShadow: 1,
									}}
								>
									<Inventory2 />
								</Box>
								<Box>
									<Typography variant="h6" sx={{ fontWeight: 600 }}>
										Select an artifact
									</Typography>
									<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
										Choose an item from the Artifacts tree on the left to view and edit it.
									</Typography>
								</Box>
								<Typography variant="caption" color="text.secondary">
									Or ask Randol in the chat panel to help create one.
								</Typography>
							</Stack>
						</Box>
					)}
				</Paper>

				{/* Chat panel on the right */}
				<PromptChatPanel
					contextId={contextId}
					area={isApplicationArtifactSelected ? 'Context-Application' : 'Context-Domain'}
					targetType={selectedOperation ? 'Operation' : selectedEventHandler ? 'EventHandler' : undefined}
					artifactContext={getArtifactChatContext(selection, selectedCodeFile?.name ?? selectedDomainTestCodeFile?.name ?? selectedApplicationCodeFile?.name)}
					onComplete={refreshCodeArtifacts}
					onAssistantMessageReceived={handleAssistantMessageReceived}
					disableDesktopVerticalInset
				/>
			</Stack>

			<ContextArtifactDialogs
				contextId={contextId}
				entityOpen={isCreatingEntity}
				onEntityOpenChange={setIsCreatingEntity}
				entityName={entityName}
				onEntityNameChange={setEntityName}
				entityDescription={entityDescription}
				onEntityDescriptionChange={setEntityDescription}
				onCreateEntity={handleCreateEntity}
				enumOpen={isCreatingEnum}
				onEnumOpenChange={setIsCreatingEnum}
				enumName={enumName}
				onEnumNameChange={setEnumName}
				enumDescription={enumDescription}
				onEnumDescriptionChange={setEnumDescription}
				onCreateEnum={handleCreateEnum}
				operationOpen={isCreatingOperation}
				onOperationOpenChange={setIsCreatingOperation}
				onCreateOperation={handleCreateOperation}
				operationExecutionTypes={operationExecutionTypes}
				availableEntities={domainEntities}
				eventHandlerOpen={isCreatingEventHandler}
				onEventHandlerOpenChange={setIsCreatingEventHandler}
				availableEvents={availableEventsToHandle}
				onCreateEventHandler={handleCreateEventHandler}
				integrationEventOpen={isCreatingIntegrationEvent}
				onIntegrationEventOpenChange={setIsCreatingIntegrationEvent}
				onCreateIntegrationEvent={handleCreateIntegrationEvent}
			/>

			<ConfirmDialog
				open={!!codeFileDeleteTarget}
				title="Delete Code File"
				message={`Delete "${codeFileDeleteTarget?.fileName ?? ''}"? This action cannot be undone.`}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleDeleteCodeFileConfirmed}
				onCancel={() => setCodeFileDeleteTarget(null)}
			/>
		</Stack>
	);
}

type NamespaceArtifactNode = {
	label: string;
	id: string;
	children: Map<string, NamespaceArtifactNode>;
	files: ArtifactItem[];
};

/**
 * Groups unfiled implementation files by their C# namespace, relative to the
 * layer namespace (for example, `...Application.Models` becomes `Models`).
 * Files already placed in user-managed ArtifactPanel folders remain there.
 */
function buildNamespacedCodeFileItems(
	files: CodeStoreFileModel[],
	layerNamespace: 'Domain' | 'Application',
	categoryId: string,
	createFileItem: (file: CodeStoreFileModel) => ArtifactItem,
): ArtifactItem[] {
	const rootNodes = new Map<string, NamespaceArtifactNode>();
	const result: ArtifactItem[] = [];

	for (const file of files) {
		const fileItem = { ...createFileItem(file), artifactType: 'Implementation' };
		if (fileItem.folderId) {
			result.push(fileItem);
			continue;
		}

		const namespaceSegments = (file.namespace ?? '').split('.').map((segment) => segment.trim()).filter(Boolean);
		const layerNamespaceAliases = layerNamespace === 'Domain' ? ['domain', 'domainmodel'] : ['application'];
		const layerIndex = namespaceSegments.findIndex((segment) => layerNamespaceAliases.includes(segment.toLocaleLowerCase()));
		const relativeSegments = layerIndex >= 0 ? namespaceSegments.slice(layerIndex + 1) : namespaceSegments;
		if (relativeSegments.length === 0) {
			result.push(fileItem);
			continue;
		}

		let currentNodes = rootNodes;
		const path: string[] = [];
		let currentNode: NamespaceArtifactNode | undefined;
		for (const segment of relativeSegments) {
			path.push(segment);
			currentNode = currentNodes.get(segment);
			if (!currentNode) {
				currentNode = {
					label: segment,
					id: `${categoryId}:namespace:${path.map((pathSegment) => encodeURIComponent(pathSegment)).join('/')}`,
					children: new Map<string, NamespaceArtifactNode>(),
					files: [],
				};
				currentNodes.set(segment, currentNode);
			}
			currentNodes = currentNode.children;
		}
		currentNode?.files.push(fileItem);
	}

const toArtifactItem = (node: NamespaceArtifactNode): ArtifactItem => ({
	id: node.id,
		label: node.label,
		icon: <NamespaceFolderIcon fontSize="small" />,
		countAsArtifact: false,
		canMoveToFolder: false,
		artifactType: 'Implementation',
	children: [...Array.from(node.children.values()).map(toArtifactItem), ...node.files].sort((left, right) => left.label.localeCompare(right.label)),
});

	return [...Array.from(rootNodes.values()).map(toArtifactItem), ...result].sort((left, right) => left.label.localeCompare(right.label));
}

function getEntitySubFilterValue(schemaTypeId: SchemaTypesModel): string {
	switch (String(schemaTypeId)) {
		case SchemaTypesModel.RootDataSchema:
			return 'root';
		case SchemaTypesModel.DataSchema:
			return 'domain';
		case SchemaTypesModel.DataOperationRequestSchema:
		case SchemaTypesModel.DataOperationResponseSchema:
		case SchemaTypesModel.DataReadSchema:
		case SchemaTypes.DTOSchema:
			return 'dto';
		default:
			return 'other';
	}
}
